import React, { useMemo, useState } from 'react';
import { FiAward, FiCheck, FiX, FiArrowRight } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import { cn } from '@/lib/utils';

// Quiz em cartas dirigido por scorm.content.cards (correção 100% local, sem API/spaced-repetition).
// Flip card INLINE (não usa o FlashcardStack do modal, cujo CSS absoluto estoura na trilha):
// pergunta → escolhe alternativa → vira (rotateY 3D) pro feedback → próxima. Fica dentro do bloco.
// cards: [{ key_element?, question, alternatives: [string], correct: string, explanation: string, taxonomy_id?: number }]
export function ScormQuizCards({ cards = [], scorm, setScormSectionDone, setFinished, backRoute = '/trilha', onComplete }) {
  const navigate = useNavigate();
  const [index, setIndex] = useState(0);
  const [showFeedback, setShowFeedback] = useState(false);
  const [selected, setSelected] = useState(null);
  const [isCorrect, setIsCorrect] = useState(false);
  const [correctCount, setCorrectCount] = useState(0);
  const [wrongCount, setWrongCount] = useState(0);
  const [isComplete, setIsComplete] = useState(false);
  // Respostas na ordem em que o aluno respondeu (o checkpoint de revisão da trilha envia como card_results).
  const [cardResults, setCardResults] = useState([]);

  const total = cards.length;
  const card = cards[index];
  const keyElement = card?.key_element || card?.['key-element'];

  // Embaralha as alternativas por carta (estável enquanto o índice não muda).
  const alternatives = useMemo(() => {
    if (!card) return [];
    return [...(card.alternatives || [])].filter(Boolean).sort(() => Math.random() - 0.5);
  }, [index]);

  if (total === 0) return null;

  const handleSelect = alt => {
    if (showFeedback) return;
    setSelected(alt);
    const correct = alt === card.correct;
    setIsCorrect(correct);
    if (correct) setCorrectCount(prev => prev + 1);
    else setWrongCount(prev => prev + 1);
    setCardResults(prev => [...prev, { taxonomy_id: card.taxonomy_id, correct }]);
    setShowFeedback(true);
  };

  const handleContinue = () => {
    if (index === total - 1) {
      setIsComplete(true);
      if (setScormSectionDone && scorm) setScormSectionDone(scorm.id, cardResults);
      if (setFinished) setFinished(true);
      return;
    }
    setShowFeedback(false);
    setSelected(null);
    setIndex(prev => prev + 1);
  };

  if (isComplete) {
    const accuracy = total > 0 ? Math.round((correctCount / total) * 100) : 0;
    return (
      <div className="mx-auto w-full max-w-md rounded-2xl border border-primary-lightest bg-white p-8 text-center shadow-dropshadow-sm">
        <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-primary-lightest text-primary">
          <FiAward className="h-8 w-8" />
        </div>
        <h2 className="text-xl font-bold text-neutral-darker">Quiz concluído!</h2>
        <div className="mt-5 flex justify-center gap-6">
          <Stat
            value={total}
            label="Total"
          />
          <Stat
            value={correctCount}
            label="Acertos"
            className="text-success"
          />
          <Stat
            value={wrongCount}
            label="Erros"
            className="text-rose-500"
          />
        </div>
        <div className="mt-5">
          <div className="h-2 w-full overflow-hidden rounded-full bg-neutral-100">
            <div
              className="h-full rounded-full bg-primary transition-[width] duration-500"
              style={{ width: `${accuracy}%` }}
            />
          </div>
          <span className="mt-1.5 block text-sm font-semibold text-neutral-500">{accuracy}% de aproveitamento</span>
        </div>
        <button
          type="button"
          onClick={() => (onComplete ? onComplete() : navigate(backRoute))}
          className="mt-6 inline-flex items-center justify-center rounded-full bg-primary px-6 py-3 font-semibold text-white transition active:scale-[0.98]"
        >
          {onComplete ? 'Concluir revisão' : 'Próximo bloco de estudos'}
        </button>
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-2xl">
      {/* progresso + placar */}
      <div className="mb-3">
        <div className="mb-1.5 flex items-center justify-between text-xs font-semibold text-neutral-500">
          <span>
            Questão {index + 1}/{total}
          </span>
          <span className="flex gap-3">
            <span className="text-success">✓ {correctCount}</span>
            <span className="text-rose-500">✗ {wrongCount}</span>
          </span>
        </div>
        <div className="h-2 w-full overflow-hidden rounded-full bg-neutral-100">
          <div
            className="h-full rounded-full bg-primary transition-[width] duration-300"
            style={{ width: `${(index / total) * 100}%` }}
          />
        </div>
      </div>

      {/* flip card (3D via style, à prova de config do Tailwind) */}
      <div style={{ perspective: '1200px' }}>
        <div
          className="relative w-full transition-transform duration-500"
          style={{
            transformStyle: 'preserve-3d',
            minHeight: 360,
            transform: showFeedback ? 'rotateY(180deg)' : 'rotateY(0deg)'
          }}
        >
          {/* FRENTE — pergunta + alternativas */}
          <div style={{ backfaceVisibility: 'hidden' }}>
            <div className="rounded-xl border border-solid border-primary-lightest bg-white p-6 shadow-dropshadow-sm">
              {keyElement && <p className="mb-2 text-[15px] leading-relaxed text-neutral-darker">{keyElement}</p>}
              <h3 className="c-scorm-quiz-question mb-4 text-base font-semibold leading-relaxed text-primary">{card.question}</h3>
              <div className="flex flex-col gap-2">
                {alternatives.map((alt, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => handleSelect(alt)}
                    disabled={showFeedback}
                    className={cn('flex items-center gap-3 rounded-lg border border-solid p-3 text-left transition', selected === alt ? 'border-primary bg-primary-lightest' : 'border-neutral-200 bg-white hover:border-primary-light')}
                  >
                    <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-neutral-100 text-sm font-bold text-neutral-600">{String.fromCharCode(65 + i)}</span>
                    <span className="text-sm text-neutral-darker">{alt}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* VERSO — feedback */}
          <div
            className="absolute inset-0"
            style={{ backfaceVisibility: 'hidden', transform: 'rotateY(180deg)' }}
          >
            <div className={cn('flex h-full flex-col rounded-xl p-6 text-white shadow-dropshadow-sm', isCorrect ? 'bg-success' : 'bg-rose-500')}>
              <div className="mb-3 flex items-center gap-3">
                <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-white/20">{isCorrect ? <FiCheck className="h-6 w-6" /> : <FiX className="h-6 w-6" />}</span>
                <h3 className="text-lg font-bold !text-white">{isCorrect ? 'Resposta correta!' : 'Resposta incorreta'}</h3>
              </div>

              {!isCorrect && (
                <div className="mb-3 rounded-lg bg-white/15 p-3">
                  <span className="block text-[11px] font-bold uppercase tracking-wide text-white opacity-80">Resposta correta</span>
                  <span className="font-semibold text-white">{card.correct}</span>
                </div>
              )}

              {card.explanation && (
                <div className="mb-4 flex-1 overflow-auto rounded-lg bg-white/15 p-3">
                  <span className="block text-[11px] font-bold uppercase tracking-wide text-white opacity-80">Explicação</span>
                  <p className="mt-1 text-sm leading-relaxed text-white">{card.explanation}</p>
                </div>
              )}

              <button
                type="button"
                onClick={handleContinue}
                className="mt-auto inline-flex items-center justify-center gap-2 rounded-full bg-white px-6 py-3 font-semibold text-neutral-darker transition active:scale-[0.98]"
              >
                {index === total - 1 ? 'Ver resultados' : 'Próxima questão'}
                <FiArrowRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Stat({ value, label, className }) {
  return (
    <div className="flex flex-col">
      <span className={cn('text-2xl font-bold text-neutral-darker', className)}>{value}</span>
      <span className="text-xs font-semibold text-neutral-400">{label}</span>
    </div>
  );
}

export default ScormQuizCards;
